import { ApolloError, MutationFunction, useMutation } from '@apollo/client';

import {
  CREATE_CASHBACK,
  DELETE_CASHBACK,
  GET_CACHED_CASHBACK,
  ICreateCashbackRequest,
  ICreateCashbackResponse,
  IDeleteCashbackRequest,
  IDeleteCashbackResponse,
  IUpdateCashbackRequest,
  IUpdateCashbackResponse,
  UPDATE_CASHBACK,
} from './schema';
import { TCashbackRule } from './types';

interface IArgs {
  onDelete?: (data: IDeleteCashbackResponse) => void;
  onUpdate?: (data: IUpdateCashbackResponse) => void;
  onCreate?: (data: ICreateCashbackResponse) => void;
}
interface IReturn {
  createCashback: MutationFunction<
    ICreateCashbackResponse,
    ICreateCashbackRequest
  >;
  updateCashback: MutationFunction<
    IUpdateCashbackResponse,
    IUpdateCashbackRequest
  >;
  deleteCashback: MutationFunction<
    IDeleteCashbackResponse,
    IDeleteCashbackRequest
  >;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  createError?: ApolloError;
  updateError?: ApolloError;
}
export const useCashbackRuleMutations = (args?: IArgs): IReturn => {
  const [createCashback, { error: createError, loading: isCreating }] =
    useMutation<ICreateCashbackResponse, ICreateCashbackRequest>(
      CREATE_CASHBACK,
      {
        onError: console.error,
        onCompleted: args?.onCreate,
        update: (cache, { data }) => {
          const rule = data?.createDepositCashback;

          if (!rule) return;

          cache.modify({
            fields: {
              depositCashbacks: (existingRules = []) => {
                const newRule = cache.writeQuery({
                  id: cache.identify(rule),
                  data: rule,
                  query: GET_CACHED_CASHBACK,
                });

                return [...existingRules, newRule].sort((r1, r2) => {
                  const amount1 =
                    cache.readQuery<TCashbackRule>({
                      id: r1.__ref,
                      query: GET_CACHED_CASHBACK,
                    })?.amount ?? 0;
                  const amount2 =
                    cache.readQuery<TCashbackRule>({
                      id: r2.__ref,
                      query: GET_CACHED_CASHBACK,
                    })?.amount ?? 0;

                  return amount1 - amount2;
                });
              },
            },
          });
        },
      }
    );

  const [updateCashback, { error: updateError, loading: isUpdating }] =
    useMutation<IUpdateCashbackResponse, IUpdateCashbackRequest>(
      UPDATE_CASHBACK,
      {
        onError: console.error,
        onCompleted: args?.onUpdate,
        update: (cache, { data }) => {
          const rule = data?.updateDepositCashback;

          if (!rule) return;

          cache.writeQuery({
            id: cache.identify(rule),
            data: rule,
            query: GET_CACHED_CASHBACK,
          });
        },
      }
    );

  const [deleteCashback, { loading: isDeleting }] = useMutation<
    IDeleteCashbackResponse,
    IDeleteCashbackRequest
  >(DELETE_CASHBACK, {
    onError: console.error,
    onCompleted: args?.onDelete,
    update: (cache, { data }) => {
      const rule = data?.deleteDepositCashback;

      if (!rule) return;

      cache.evict({
        id: cache.identify(rule),
      });
      cache.gc();
    },
  });

  return {
    deleteCashback,
    updateCashback,
    createCashback,
    isDeleting,
    isUpdating,
    isCreating,
    createError,
    updateError,
  };
};
